/**
 * Copyright Highway9 Networks Inc.
 */
import {
  PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "..";
import { Types } from "../../constants/types";
import { subscriberService } from "../../services";
import { DeviceGroup } from "../../types/deviceGroup";
import { createSocketPatchReducer } from "../events/socketPatch";
import { Device } from "~/types/device";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

const deviceGroupsAdapter = createEntityAdapter<DeviceGroup>({
  selectId: (group) => group.id!,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

interface DeviceGroupState {
  groupsData: EntityState<DeviceGroup>;
  selectedId: string | null;
  open: boolean;
  deviceAdministrationOpen: boolean;
  isEdit: boolean;
  selectedDeviceGroupIDs: string[];
  selectedDevices: Device[];
  showSyntheticSims: boolean;
}

export const defaultDeviceGroup: DeviceGroup = {
  name: "",
  activePolicies: [],
  ueAmbr: {
    uplink: 0,
    downlink: 0,
  },
  vmzZones: [],
};

const initialState: DeviceGroupState = {
  groupsData: deviceGroupsAdapter.getInitialState(),
  selectedId: null,
  open: false,
  deviceAdministrationOpen: false,
  selectedDeviceGroupIDs: [Types.all],
  isEdit: false,
  selectedDevices: [],
  showSyntheticSims: false,
};

export const fetchDeviceGroups = createAsyncThunk(
  "deviceGroup/fetchDeviceGroups",
  async () => subscriberService.getDeviceGroups(),
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; deviceGroups payload not surfaced in KPI UI
  // (called from VMZ admin + useDevicePage subscribers page + websocket refreshDeviceGroups on connect)
  { condition: () => !isPartnerKpiOnlyUser() },
);

const deviceGroupSlice = createSlice({
  name: "deviceGroups",
  initialState,
  reducers: {
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setDeviceGroupOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setDeviceAdministrationOpen: (state, action: PayloadAction<boolean>) => {
      state.deviceAdministrationOpen = action.payload;
    },
    setSelectedDeviceGroupIDs: (state, action: PayloadAction<string[]>) => {
      state.selectedDeviceGroupIDs = action.payload;
    },
    setEdit: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    setSelectedDevices: (state, action: PayloadAction<Device[]>) => {
      state.selectedDevices = action.payload;
    },
    setShowSyntheticSims: (state, action: PayloadAction<boolean>) => {
      state.showSyntheticSims = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(deviceGroupsAdapter, (s) => s.groupsData),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDeviceGroups.fulfilled, (state, action) => {
      deviceGroupsAdapter.setAll(state.groupsData, action.payload);
    });
  },
});

export const deviceGroupActions = deviceGroupSlice.actions;
export default deviceGroupSlice.reducer;

const deviceGroupEntitySelectors = deviceGroupsAdapter.getSelectors(
  (state: RootState) => state.deviceGroup.groupsData,
);

export const deviceGroupData = deviceGroupEntitySelectors.selectAll;
export const deviceGroupEntities = deviceGroupEntitySelectors.selectEntities;

export const subscriberDeviceGroups = createSelector(
  [deviceGroupData, (state: RootState) => state.deviceGroup.showSyntheticSims],
  (data, showSyntheticSims) =>
    data.filter((group) => !group.isSynthetic || showSyntheticSims),
);

export const deviceGroupState = createSelector(
  [
    (state: RootState) => state.deviceGroup.selectedId,
    deviceGroupEntities,
  ],
  (selectedId, entities): DeviceGroup | null =>
    selectedId ? (entities[selectedId] ?? null) : null,
);

export const deviceGroupOpen = (state: RootState) => state.deviceGroup.open;
export const deviceGroupDeviceAdministrationOpen = (state: RootState) => state.deviceGroup.deviceAdministrationOpen;
export const deviceGroupSelectedIDs = (state: RootState) => state.deviceGroup.selectedDeviceGroupIDs;
export const deviceGroupIsEdit = (state: RootState) => state.deviceGroup.isEdit;
export const deviceGroupSelectedDevices = (state: RootState) => state.deviceGroup.selectedDevices;
export const showSyntheticSimsSelector = (state: RootState) => state.deviceGroup.showSyntheticSims;
